import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Zap, Clock, Star, Phone, Megaphone, TrendingUp, Volume2, VolumeX, Maximize2 } from 'lucide-react';

const Home = () => {
  const [isMuted, setIsMuted] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (window.ml) {
      window.ml('accounts', '2431868', 'load');
    }
  }, []);

  useEffect(() => {
    // Load Elfsight platform script
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const faqs = [
    {
      q: 'How long does setup take?',
      a: 'Typically 5 to 10 business days from brief sign-off to go-live.'
    },
    {
      q: 'Do I need any technical knowledge?',
      a: 'None at all. We handle everything. You just review and approve the agent before it goes live.'
    },
    {
      q: 'What if I already have a receptionist or sales team?',
      a: 'Our AI works alongside your team, handling overflow, out-of-hours, and routine enquiries so your people can focus on higher-value work.'
    },
    {
      q: 'Is there a long contract?',
      a: 'Starter and Growth plans have a 3-month minimum. Scale is 6 months. After that it rolls monthly.'
    },
    {
      q: 'What industries do you work with?',
      a: 'Any business that receives inbound calls or has a lead database — trades, clinics, estate agents, coaches, service businesses.'
    },
    {
      q: "What if the AI doesn't perform?",
      a: 'We have a written satisfaction guarantee. If the agent fails to meet the agreed brief, your setup fee is refunded within 14 business days.'
    },
  ];
  const videoRef = useRef(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  const features = [
    {
      icon: Database,
      title: 'Database Reactivation',
      description: 'Pull fresh sales from leads you\'ve already paid for and haven\'t bought, using conversational AI',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Zap,
      title: 'Speed To Lead',
      description: 'Stop losing sales to competitors who are faster to the phone, by getting AI to work your leads 24/7',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Clock,
      title: 'Out Of Hours',
      description: 'Stop \'Out of Hours\' leads going unanswered and wasting time each morning on unqualified prospects with AI',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Star,
      title: 'Google Reviews',
      description: 'Improve business credibility and customer opinion replying to reviews without spending man-hours using AI',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: Phone,
      title: 'Voice',
      description: 'Use AI to handle inbound calls to redirect leads, take information and book / rearrange appointments',
      gradient: 'from-indigo-500 to-blue-500'
    },
    {
      icon: Megaphone,
      title: 'AI Advertisements',
      description: 'Professional Business Advertisements without the extra production, cost or time made to look human-like using AI',
      gradient: 'from-pink-500 to-rose-500'
    }
  ];


  return (
    <div className="pt-16">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-accent-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-primary-200">
            <span className="text-primary-600 font-semibold text-sm">🚀 AI-Powered Business Solutions</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight">
            <span className="text-gradient">AI Agency</span>
            <br />
            <span className="text-gray-900">That Delivers Results</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            We're in the business of getting our client's results. We simply leverage the power of AI to do that. Our mission is to pull in multiple <span className="font-bold text-primary-600">£1000s in extra sales</span> for our clients without asking them to do any extra work.
          </p>
          
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            We use our AI services to streamline sales processes, increase productivity and turn time-sucking tasks into profit centers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/contact"
              className="group bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center space-x-2"
            >
              <span>Get Started Now</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/solutions"
              className="px-8 py-4 rounded-full font-semibold text-lg border-2 border-primary-600 text-primary-600 hover:bg-primary-50 transition-all duration-300"
            >
              View Solutions
            </Link>
          </div>
        </div>
      </section>

      {/* Video Trailer Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-primary-300 font-semibold text-sm">🎬 See AI In Action</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 text-white">
              Watch How It Works
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              See our AI solutions transform businesses in real-time
            </p>
          </div>

          {/* Video Container */}
          <div className="relative max-w-4xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-gray-800">
              <video 
                ref={videoRef}
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full h-full object-cover"
              >
                <source src="/landing-page-vid-0209.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {/* Video Controls */}
              <div className="absolute bottom-4 right-4 flex space-x-2">
                <button
                  onClick={toggleMute}
                  className="p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-300 backdrop-blur-sm border border-white/20 hover:scale-110"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all duration-300 backdrop-blur-sm border border-white/20 hover:scale-110"
                  title="Fullscreen"
                >
                  <Maximize2 className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl opacity-20 blur-xl"></div>
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-accent-500 to-pink-500 rounded-xl opacity-20 blur-xl"></div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-300 text-lg">Hear your AI agent handle real calls, 24 hours a day.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              <span className="text-gradient">Powerful AI Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transform your business with cutting-edge AI technology designed to maximize revenue and minimize effort
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover-lift border border-gray-100"
              >
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.gradient} mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4 text-gray-900">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Make More Sales & Get Better Qualified Leads
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find out if you can remove expensive bottlenecks and add a few extra zeros to your business account with AI. Book in a call here, so we can chat.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2">
              <div className="bg-gradient-to-br from-primary-600 to-accent-600 p-12 text-white flex flex-col justify-center">
                <TrendingUp className="w-16 h-16 mb-6" />
                <h3 className="text-3xl font-display font-bold mb-4">
                  Ready to get the perks of a 24/7 sales team WITHOUT the expense?
                </h3>
                <p className="text-primary-100 text-lg">
                  Let us show you how it's done
                </p>
              </div>
              <div className="p-12 flex flex-col justify-center">
                <p className="text-gray-600 mb-6 leading-relaxed">
                  If a picture is worth a thousand words, a demo is worth a thousand pictures. That's why before you do anything, I want you to see AI in action.
                </p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  With AI freeing up valuable man-power and doing the work you'd "never get around to doing", you can unlock new levels of ROI whilst focusing on the things you enjoy.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Schedule a Call
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-3 text-gray-900">What Our Clients Say</h2>
          </div>
          <div className="elfsight-app-fc7e0559-066d-4ae4-9442-a41b67645a3a" data-elfsight-app-lazy></div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-primary-50 rounded-full border border-primary-200">
            <span className="text-primary-600 font-semibold text-sm">✨ Pricing Tailored to Your Business</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-gray-900">
            We don&apos;t do off-the-shelf packages
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Because no two businesses are the same. Your quote is based on your call volume, team size, and what you need your AI agent to do. Book a free call and we&apos;ll put together your personalised quote.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Get Your Free Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
            Pull 5 or 6 Figures Worth of Sales from Old Leads
          </h2>
          <p className="text-xl text-primary-100 mb-8 leading-relaxed">
            Our Database Reactivation bot helps businesses treat every lead like it's their last. The bot takes all the people in a database who haven't bought and strikes up a conversation with them via SMS.
          </p>
          <p className="text-xl text-primary-100 mb-10 leading-relaxed">
            Then next thing you know, they're booking calls and you're up <span className="font-bold text-white">£10K-£100K+ in sales</span>.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Get Started Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaq === i ? 'max-h-96' : 'max-h-0'}`}>
                  <p className="px-6 pb-5 text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MailerLite Newsletter */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-white">Stay Ahead of the AI Curve &mdash; Get Daily AI Tips</h2>
          <p className="text-gray-300 mb-8 text-lg">Join our free daily newsletter &mdash; no jargon, just practical AI strategies you can use in your business.</p>
          <div className="flex justify-center">
            <div className="ml-embedded" data-form="0nbUbI"></div>
          </div>
          <p className="text-gray-500 text-sm mt-6">No spam. Unsubscribe anytime. Your data is kept private.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
